import React, { useState } from 'react';
import styled from 'styled-components';
import { COLORS } from '../styles/constants';
import { FaEnvelope, FaPhone, FaCheckCircle } from 'react-icons/fa';

const ContactContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #032a40 0%, #001824 100%);
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  padding: 120px 40px 80px;
  gap: 40px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    padding: 100px 20px 60px;
  }
`;

const ContactInfo = styled.div`
  color: white;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding-left: 10%;
  
  @media (max-width: 1024px) {
    align-items: center;
    text-align: center;
    padding: 0 20px;
  }
`;

const ContactTitle = styled.h1`
  font-size: 2.8rem;
  margin-bottom: 60px;
  line-height: 1.3;
  max-width: 600px;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 40px;
    text-align: center;
  }
`;

const ContactDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 40px;
  
  @media (max-width: 1024px) {
    flex-direction: row;
    justify-content: center;
    gap: 30px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 12px 20px;
  border-left: 3px solid ${COLORS.primary};
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateX(5px);
    
    @media (max-width: 1024px) {
      transform: translateY(-2px);
    }
  }
  
  a {
    color: white;
    text-decoration: none;
    font-size: 1.2rem;
    
    &:hover {
      color: ${COLORS.primary};
    }
  }
  
  svg {
    color: ${COLORS.primary};
    font-size: 24px;
  }
`;

const FormContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 0 40px;
  
  @media (max-width: 1024px) {
    padding: 0 20px;
  }
`;

const Form = styled.form`
  width: 100%;
  max-width: 800px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  padding: 40px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
`;

const FormRow = styled.div`
  margin-bottom: 15px;
  
  &:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    padding-bottom: 15px;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
  
  &:focus {
    outline: none;
    border-color: ${COLORS.primary};
    background: rgba(255, 255, 255, 0.05);
    box-shadow: 0 0 0 3px rgba(0, 144, 192, 0.1);
  }
`;

const InputGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
  color: white;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  transition: all 0.3s ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
  
  &:focus {
    outline: none;
    border-color: ${COLORS.primary};
    background: rgba(255, 255, 255, 0.05);
    box-shadow: 0 0 0 3px rgba(0, 144, 192, 0.1);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background-color: ${COLORS.primary};
  color: white;
  padding: 16px;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #0090C0;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 144, 192, 0.2);
  }
  
  &:disabled {
    background-color: #677c88;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: rgba(39, 174, 96, 0.1);
  color: #2ecc71;
  padding: 12px 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  
  svg {
    font-size: 20px;
  }
`;

const ErrorMessage = styled.div`
  background-color: rgba(231, 76, 60, 0.1);
  color: #e74c3c;
  padding: 12px 20px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const Contact = () => {
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get the current domain for the redirect URL
  const domain = window.location.origin;

  return (
    <ContactContainer>
      <ContactInfo>
        <ContactTitle>
          Let's discuss your business goals and next steps to achieving them
        </ContactTitle>
        <ContactDetails>
          <ContactItem>
            <FaPhone size={24} />
            <a href="tel:514-757-6845">514-757-6845</a>
          </ContactItem>
        </ContactDetails>
      </ContactInfo>
      
      <FormContainer>
        <Form action="https://formsubmit.co/jonathan.nardozza@jnarconsulting.com" method="POST">
          {/* FormSubmit configuration */}
          <input type="hidden" name="_subject" value="New JNAR Website Contact" />
          <input type="hidden" name="_template" value="table" />
          <input type="hidden" name="_captcha" value="false" />
          <input type="hidden" name="_next" value={`${domain}/thankyou`} />
          <input type="text" name="_honey" style={{ display: 'none' }} />
          <input type="hidden" name="_autoresponse" value="Thank you for contacting JNAR! We've received your message and will get back to you soon." />
          
          {formStatus.submitted && formStatus.success && (
            <SuccessMessage>
              <FaCheckCircle />
              <span>Thank you! Your message has been sent.</span>
            </SuccessMessage>
          )}
          
          {formStatus.submitted && !formStatus.success && (
            <ErrorMessage>{formStatus.message}</ErrorMessage>
          )}
          
          <FormRow>
            <Input type="text" id="name" name="name" placeholder="Your name" required />
          </FormRow>
          <FormRow>
            <Input type="email" id="email" name="email" placeholder="Your email" required />
          </FormRow>
          <FormRow>
            <Input type="text" id="company" name="company" placeholder="Your company" />
          </FormRow>
          <FormRow>
            <InputGroup>
              <div>
                <Input type="tel" id="phone" name="phone" placeholder="Your phone" />
              </div>
              <div>
                <Input type="text" id="country" name="country" placeholder="Your country" />
              </div>
            </InputGroup>
          </FormRow>
          <FormRow>
            <TextArea 
              id="message" 
              name="message" 
              placeholder="Tell us about your project/business need" 
              required 
            />
          </FormRow>
          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Submit'}
          </SubmitButton>
        </Form>
      </FormContainer>
    </ContactContainer>
  );
};

export default Contact;